import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { Building2, LayoutDashboard, Package, ShieldCheck, Ticket } from "lucide-react";
import { useState, type SyntheticEvent, type ReactNode } from "react";
import { UserMenu } from "@/components/app-shell";
import { ImpersonationBanner } from "@/components/billing/banners";
import { FormField } from "@/components/form-field";
import { Logo } from "@/components/logo";
import { ErrorState, LoadingScreen } from "@/components/states";
import { ThemeToggle } from "@/components/theme-toggle";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ApiRequestError, errorMessage } from "@/lib/api";
import { billingApi, billingQueries } from "@/lib/billing-api";
import { queries } from "@/lib/queries";

/**
 * Fase 9 — painel do dono do SaaS. A proteção de verdade está no backend (`/api/admin/*`); aqui só
 * guiamos: quem não é super admin vê "não encontrado", quem não tem 2FA é levado a ativar, e o
 * código do aplicativo é pedido a cada 12 h por sessão.
 */
export const Route = createFileRoute("/admin")({
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.query({ ...queries.me(), staleTime: 60_000 });
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 401) {
        throw redirect({ to: "/entrar", search: { redirecionar: location.href } });
      }
      throw err;
    }
  },
  component: AdminLayout,
});

const NAV = [
  { to: "/admin", label: "Visão geral", icon: LayoutDashboard, exact: true },
  { to: "/admin/workspaces", label: "Workspaces", icon: Building2, exact: false },
  { to: "/admin/planos", label: "Planos", icon: Package, exact: false },
  { to: "/admin/cupons", label: "Cupons", icon: Ticket, exact: false },
] as const;

function AdminLayout() {
  const me = useQuery(billingQueries.adminMe());
  if (me.isPending) return <LoadingScreen label="Abrindo o painel…" />;
  if (me.isError) {
    if (me.error instanceof ApiRequestError && me.error.status === 404) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-6xl font-semibold tracking-tight">404</p>
          <p className="text-muted-foreground">Esta página não existe.</p>
          <Button asChild>
            <Link to="/app">Voltar para o app</Link>
          </Button>
        </div>
      );
    }
    return <ErrorState error={me.error} onRetry={() => void me.refetch()} />;
  }
  if (!me.data.twoFactorEnabled) return <Need2fa />;
  if (!me.data.elevated) return <Elevate />;
  return (
    <div className="flex min-h-screen flex-col">
      <ImpersonationBanner />
      <header className="flex h-14 items-center gap-3 border-b px-4">
        <Logo />
        <Badge variant="destructive">Dono do SaaS</Badge>
        <nav className="ml-4 flex gap-1 overflow-x-auto" aria-label="Painel do dono">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              activeOptions={{ exact: item.exact }}
              className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent [&.active]:bg-accent [&.active]:font-medium [&.active]:text-foreground"
            >
              <item.icon className="size-4" aria-hidden />
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="flex-1" />
        <Button asChild variant="ghost" size="sm">
          <Link to="/app">Ir para o app</Link>
        </Button>
        <ThemeToggle />
        <UserMenu />
      </header>
      <main className="flex-1 p-4 md:p-8">
        <div className="mx-auto w-full max-w-6xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-muted/40">
      <header className="flex items-center justify-between px-6 py-4">
        <Logo />
        <ThemeToggle />
      </header>
      <main className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-md">{children}</div>
      </main>
    </div>
  );
}

function Need2fa() {
  return (
    <Shell>
      <Card>
        <CardHeader>
          <CardTitle>Ative a verificação em duas etapas</CardTitle>
          <CardDescription>
            O painel do dono dá acesso a todos os workspaces, por isso exige 2FA com aplicativo autenticador.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild>
            <Link to="/app/conta">
              <ShieldCheck />
              Configurar 2FA
            </Link>
          </Button>
        </CardContent>
      </Card>
    </Shell>
  );
}

function Elevate() {
  const [code, setCode] = useState("");
  const queryClient = useQueryClient();
  const elevate = useMutation({
    mutationFn: () => billingApi.elevate(code),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin"] });
    },
  });
  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    elevate.mutate();
  };
  return (
    <Shell>
      <Card>
        <CardHeader>
          <CardTitle>Confirme que é você</CardTitle>
          <CardDescription>Digite o código do aplicativo autenticador. Vale por 12 horas nesta sessão.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            {elevate.isError ? (
              <Alert variant="destructive">
                <AlertDescription>{errorMessage(elevate.error)}</AlertDescription>
              </Alert>
            ) : null}
            <FormField id="codigo-admin" label="Código de 6 números">
              <Input id="codigo-admin" autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
            </FormField>
            <Button type="submit" className="w-full" loading={elevate.isPending} disabled={code.length !== 6}>
              Abrir o painel
            </Button>
          </form>
        </CardContent>
      </Card>
    </Shell>
  );
}
